import React, { useState, useMemo, useRef, useEffect } from 'react';
import { clinicService } from '../../../services/api';
import ClinicSearch from '../ClinicSearch';

const Step3_Preview = ({
  mode = 'text',
  results,
  clid,
  onBack,
  onComplete,
  onRestart,
  loading,
  error,
  setLoading,
  setError
}) => {
  const [clinicId, setClinicId] = useState(clid || '');
  const buildInitialQaList = () => {
    if (!results || results.length === 0) return [];
    if (mode === 'pricing') {
      return results
        .filter(r => r.success && r.data && Array.isArray(r.data.qa_pairs) && r.data.qa_pairs.length > 0)
        .flatMap(r =>
          r.data.qa_pairs.map(qa => ({
            ...qa,
            sourceUrl: r.sourceLabel || r.url || r.fileName || 'Image source'
          }))
        );
    }
    return results
      .filter(r => r.success && Array.isArray(r.data) && r.data.length > 0)
      .flatMap(r => r.data.map(qa => ({ ...qa, sourceUrl: r.url })));
  };

  const [qaList, setQaList] = useState(buildInitialQaList());
  const [bulkTags, setBulkTags] = useState('');
  const [bulkLanguage, setBulkLanguage] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const abortControllerRef = useRef(null);

  // Extract failed results for display
  const failedResults = useMemo(() => {
    if (mode === 'pricing') {
      return results.filter(r => {
        if (!r.success || !r.data) return true;
        const pricingCount = r.data?.pricing_items?.length || 0;
        const qaCount = r.data?.qa_pairs?.length || 0;
        return pricingCount + qaCount === 0;
      });
    }
    return results.filter(r => !r.success || !Array.isArray(r.data) || r.data.length === 0);
  }, [results, mode]);

  const pricingItems = useMemo(() => {
    if (mode !== 'pricing') return [];
    return results
      .filter(r => r.success && r.data && Array.isArray(r.data.pricing_items) && r.data.pricing_items.length > 0)
      .flatMap(r =>
        r.data.pricing_items.map(item => ({
          ...item,
          sourceUrl: r.sourceLabel || r.url || r.fileName || 'Image source'
        }))
      );
  }, [results, mode]);

  useEffect(() => {
    setQaList(buildInitialQaList());
  }, [results, mode]);

  useEffect(() => {
    return () => {
      // Cancel ongoing requests if component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleRemoveQA = (index) => {
    setQaList(qaList.filter((_, i) => i !== index));
  };

  const handleUpdateQA = (index, field, value) => {
    const newList = [...qaList];
    if (field === 'tags') {
      // Handle tags as array
      newList[index].tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
    } else {
      newList[index][field] = value;
    }
    setQaList(newList);
  };

  const hasQaEntries = qaList.length > 0;

  const applyTagsToAll = () => {
    const tags = bulkTags
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);

    if (tags.length === 0) {
      alert('Please enter at least one tag before applying.');
      return;
    }

    if (!hasQaEntries) {
      alert('No Q&A pairs available to update.');
      return;
    }

    setQaList(prev => prev.map(qa => ({ ...qa, tags })));
  };

  const applyLanguageToAll = () => {
    const language = bulkLanguage.trim();
    if (!language) {
      alert('Please enter a language code before applying.');
      return;
    }

    if (!hasQaEntries) {
      alert('No Q&A pairs available to update.');
      return;
    }

    setQaList(prev => prev.map(qa => ({ ...qa, language })));
  };

  // Prepare QA data for export
  const allQA = useMemo(() => {
    return qaList.map((qa) => ({
      language: qa.language || 'zh_HK',
      question: qa.question,
      answer: qa.answer,
      tags: qa.tags || ['保險及直接結算']
    }));
  }, [qaList]);

  // Memoize JSON string to avoid recalculating on every render
  const jsonPreview = useMemo(() => {
    return JSON.stringify(allQA, null, 2);
  }, [allQA]);

  const pricingJsonPreview = useMemo(() => {
    if (mode !== 'pricing') return '';
    return JSON.stringify(pricingItems, null, 2);
  }, [mode, pricingItems]);

  const handleRestartFlow = () => {
    if (onRestart) {
      onRestart();
    } else if (onBack) {
      onBack();
    }
  };

  const handleImportQA = async () => {
    if (!clinicId.trim()) {
      setError('Please enter a Clinic ID');
      return;
    }

    if (qaList.length === 0) {
      setError('No QA pairs to import');
      return;
    }

    setImporting(true);
    setError('');
    setImportResults(null);
    setImportStatus(null);

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      // Prepare QA items for import
      const qaItems = qaList.map(qa => ({
        language: qa.language || 'zh_HK',
        question: qa.question,
        answer: qa.answer,
        tags: Array.isArray(qa.tags) ? qa.tags : (qa.tags ? [qa.tags] : ['保險及直接結算'])
      }));

      // Direct API call - already async and non-blocking
      const result = await clinicService.importQAItems(clinicId, qaItems);

      // Check if request was cancelled
      if (signal.aborted) {
        return;
      }

      const createdCount = result.created_count || qaItems.length;

      setImportResults({
        success: true,
        created_count: createdCount,
        errors: result.failed_items || []
      });

      if (createdCount > 0) {
        onComplete &&
          onComplete({
            success: true,
            created_count: createdCount,
            clid: clinicId || clid,
            mode
          });
        return;
      }

      setError('Failed to import QA items. Please check the errors below.');
      setImportResults({
        success: false,
        created_count: 0,
        errors: result.failed_items || ['No items were imported']
      });
      setImportStatus({
        success: false,
        createdCount: 0,
        errors: result.failed_items || ['No items were imported']
      });
    } catch (err) {
      // Check if request was cancelled
      if (signal.aborted || err.name === 'CanceledError' || err.name === 'AbortError') {
        return;
      }

      console.error('Error importing QA items:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to import QA items';
      setError(errorMsg);
      setImportResults({
        success: false,
        created_count: 0,
        errors: [errorMsg]
      });
      setImportStatus({
        success: false,
        createdCount: 0,
        errors: [errorMsg]
      });
    } finally {
      if (!signal.aborted) {
        setImporting(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 3: Preview and Adjust</h2>
        <p className="text-gray-600">Review the generated Q&A pairs and make any necessary adjustments</p>
      </div>

      {/* Clinic ID Input with Search */}
      <ClinicSearch
        selectedClinicId={clinicId}
        onClinicSelect={setClinicId}
        error={error}
        setError={setError}
      />

      {/* Pricing Items */}
      {mode === 'pricing' && pricingItems.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Pricing Items ({pricingItems.length})
            </h3>
            <button
              onClick={() => navigator.clipboard.writeText(pricingJsonPreview)}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
            >
              Copy Pricing JSON
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Service Item</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Price</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Currency</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Notes</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Source</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pricingItems.map((item, index) => (
                  <tr key={`${item.service_item}-${index}`}>
                    <td className="px-4 py-2 text-gray-900">{item.service_item}</td>
                    <td className="px-4 py-2 text-gray-900">{item.price}</td>
                    <td className="px-4 py-2 text-gray-900">{item.currency || '—'}</td>
                    <td className="px-4 py-2 text-gray-700">{item.notes || '—'}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{item.sourceUrl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Metadata Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Metadata</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma separated)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={bulkTags}
                onChange={(e) => setBulkTags(e.target.value)}
                placeholder="e.g. 保險及直接結算, 預約"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={applyTagsToAll}
                disabled={!hasQaEntries}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply to All
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Sets the same tag list on every Q&A pair in this preview.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language Code
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={bulkLanguage}
                onChange={(e) => setBulkLanguage(e.target.value)}
                placeholder="e.g. zh_HK, en_US"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={applyLanguageToAll}
                disabled={!hasQaEntries}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply to All
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Overrides the language of every Q&A entry shown below.
            </p>
          </div>
        </div>
      </div>

      {/* QA List */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Q&A Pairs ({qaList.length})
          </h3>
        </div>
        <div className="space-y-4">
          {qaList.map((qa, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 relative">
              <button
                onClick={() => handleRemoveQA(index)}
                className="absolute top-4 right-4 text-red-600 hover:text-red-700"
                title="Remove Q&A"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                  <input
                    type="text"
                    value={qa.question || ''}
                    onChange={(e) => handleUpdateQA(index, 'question', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                  <textarea
                    value={qa.answer || ''}
                    onChange={(e) => handleUpdateQA(index, 'answer', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                    <select
                      value={qa.language || 'zh_HK'}
                      onChange={(e) => handleUpdateQA(index, 'language', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="zh_HK">繁體中文 (zh_HK)</option>
                      <option value="zh_CN">简体中文 (zh_CN)</option>
                      <option value="en_US">English (en_US)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={Array.isArray(qa.tags) ? qa.tags.join(', ') : (qa.tags || '保險及直接結算')}
                      onChange={(e) => handleUpdateQA(index, 'tags', e.target.value)}
                      placeholder="保險及直接結算"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                {qa.sourceUrl && (
                  <div className="text-xs text-gray-500">
                    Source: <span className="truncate">{qa.sourceUrl}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {qaList.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No QA pairs to preview. Please go back and try again.
          </div>
        )}
      </div>

      {/* Failed URLs Section - NEW */}
      {failedResults.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-4">
            Failed URLs ({failedResults.length})
          </h3>
          <div className="space-y-3">
            {failedResults.map((result, index) => (
              <div key={index} className="bg-white border border-red-300 rounded-md p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-red-900 mb-1 break-words">
                      Source:{' '}
                      <span className="font-mono text-xs">
                        {result.sourceLabel || result.url || result.fileName || 'Unknown Source'}
                      </span>
                    </div>
                    <div className="text-sm text-red-700">
                      <span className="font-medium">Error: </span>
                      {result.error || 'Unknown error occurred'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw JSON Preview */}
      {qaList.length > 0 && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Raw JSON Preview</h3>
            <button
              onClick={() => {
                // Use memoized JSON to avoid blocking
                navigator.clipboard.writeText(jsonPreview).then(() => {
                  alert('JSON copied to clipboard!');
                }).catch(err => {
                  console.error('Failed to copy JSON:', err);
                });
              }}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy JSON
            </button>
          </div>
          <div className="bg-white border border-gray-300 rounded-md p-4 overflow-auto max-h-96">
            <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
              {jsonPreview}
            </pre>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-medium">{error}</div>
        </div>
      )}

      {/* Import Results */}
      {importResults && importResults.errors && importResults.errors.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold mb-2">⚠ Import Completed with Errors</h4>
          <div className="text-sm">
            <div>Created {importResults.created_count || 0} QA item(s)</div>
            <div className="mt-2 font-medium text-yellow-800">Errors:</div>
            <ul className="list-disc list-inside text-yellow-700">
              {importResults.errors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {importStatus && !importStatus.success && (
        <div className="border rounded-lg p-4 bg-red-50 border-red-200">
          <h4 className="font-semibold mb-2">⚠ Import Failed</h4>
          <p className="text-sm text-gray-800">
            Please review the errors above and adjust the Q&A pairs if necessary.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleRestartFlow}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Start New Import
            </button>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={importing}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Back
        </button>
        <button
          onClick={handleImportQA}
          disabled={qaList.length === 0 || !clinicId.trim() || importing || loading}
          className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {importing ? 'Importing...' : 'Complete & Import QA Items'}
        </button>
      </div>
    </div>
  );
};

export default Step3_Preview;

